import {
  Swimlane as RMSwimlane,
  useSwimlaneApiRef,
  useSwimlaneEventHandler,
} from '@/components/ui/swimlane';
import { TITLE_HEIGHT_TO_LINES } from '../swimlane/utils/constants';
import { SWIMLANE_LIMITS } from './constants';
import {
  NO_OP_ACTIONS,
  ReadOnlyColumnHeader,
  ReadOnlyGroupRowHeader,
  ReadOnlyRowHeader,
} from './shared/swimlane-headers';
import type { PublishedSwimlaneResponse } from './types';

interface PublishedSwimlaneProps {
  /** Pre-fetched swimlane data from parent - avoids duplicate API calls */
  data: PublishedSwimlaneResponse;
  onItemClick?: (itemId: string) => void;
  /** Enable transparent background for PNG export */
  transparentBackground?: boolean;
  /** Control sticky headers - defaults to true for normal published views, false for export previews */
  isHeadersSticky?: boolean;
}

export function PublishedSwimlane({
  data,
  onItemClick,
  transparentBackground = false,
  isHeadersSticky = true,
}: PublishedSwimlaneProps) {
  const apiRef = useSwimlaneApiRef();

  // Subscribe to openCardDetails event to handle item clicks
  useSwimlaneEventHandler(apiRef, 'openCardDetails', (params) => {
    if (onItemClick && params) {
      onItemClick(params.itemID);
    }
  });

  const items = new Map(Object.entries(data.items));

  const cells = new Map(
    Object.entries(data.cells).map(([rowId, cols]) => [rowId, new Map(Object.entries(cols))]),
  );

  // Filter out hidden headers (same pattern as useSwimlaneData)
  const columnHeaders =
    data.columnHiddenHeaderIds && data.columnHiddenHeaderIds.length > 0
      ? data.columnHeaders.filter((header) => {
          const hiddenSet = new Set(data.columnHiddenHeaderIds);
          return !hiddenSet.has(header.id);
        })
      : data.columnHeaders;

  const rowHeaders =
    data.rowHiddenHeaderIds && data.rowHiddenHeaderIds.length > 0
      ? data.rowHeaders.filter((header) => {
          const hiddenSet = new Set(data.rowHiddenHeaderIds);
          return !hiddenSet.has(header.id);
        })
      : data.rowHeaders;

  const layout = data.layout;
  const rowHeaderOrientation = layout.headerDirection;
  const isCompact = layout.theme === 'compact';
  const areLabelsEnabled = !!(layout.labelByHeader1 || layout.labelByHeader2);

  const backgroundColor = 'var(--export-bg-color, white)';

  return (
    <div className="h-full w-full px-4 py-2" data-transparent-background={transparentBackground}>
      <RMSwimlane
        isPublicationMode={true}
        readOnly={true}
        actions={NO_OP_ACTIONS}
        showExpandedSubItems={true}
        columnHeaders={columnHeaders}
        rowHeaders={rowHeaders}
        items={items}
        cells={cells}
        emptyState={null}
        isAddColumnHeaderButtonVisible={false}
        isAddRowHeaderButtonVisible={false}
        isCompact={isCompact}
        isHeadersSticky={isHeadersSticky}
        isItemDateVisible={data.layout.showItemDates ?? true}
        isItemDescriptionVisible={data.layout.showDescription ?? true}
        isItemParentVisible={data.layout.showParent ?? true}
        isItemDetailsVisible={true}
        itemLabelSize={layout.titleSize ?? 'medium'}
        maxCardTitleLines={TITLE_HEIGHT_TO_LINES[layout.titleHeight ?? 'default']}
        rowHeaderOrientation={rowHeaderOrientation}
        rowHeadersWidth={SWIMLANE_LIMITS.rowHeadersWidth}
        showGridLines={layout.showGridLines ?? true}
        areLabelsEnabledInCompactSL={areLabelsEnabled}
        apiRef={apiRef}
        maxSelectedCards={SWIMLANE_LIMITS.maxSelectedCards}
        maxSubItemsPerItem={SWIMLANE_LIMITS.maxSubItemsPerItem}
        isSubItemFeaturesEnabled={true}
        columnAxisLabelText={data.columnAxisLabel}
        rowAxisLabelText={data.rowAxisLabel || null}
        backgroundColor={backgroundColor}
        slots={{
          columnHeader: ReadOnlyColumnHeader,
          rowHeader: ReadOnlyRowHeader,
          groupRowHeader: ReadOnlyGroupRowHeader,
        }}
      />
    </div>
  );
}
